import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import LockResetOutlined from "@mui/icons-material/LockResetOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useBrand } from "../../context/BrandContext";
import MarketingMeasurementScripts from "../../components/MarketingMeasurementScripts";
import { readStoredLocale, setUiLocale, getGlobalLocalesForUiSelect } from "../../i18n/config";
import { parseUiLocaleFromSearch } from "../../i18n/urlLocale";
import { getLoginUiStrings, getLoginLocaleLabel } from "../../i18n/loginUiStrings";
import { apiClient } from "../../api/client";

const COMPANY_RE = /^[a-zA-Z0-9._-]{2,32}$/;
const toSlug = (v) => String(v || "").trim().toLowerCase();

function applyTemplate(s, vars) {
  return String(s || "").replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ""));
}

function CheckInboxBody({ template, email }) {
  const parts = String(template || "").split("{{email}}");
  if (parts.length === 1) {
    return applyTemplate(template, { email });
  }
  return (
    <>
      {parts[0]}
      <Box component="strong" sx={{ wordBreak: "break-all" }}>
        {email}
      </Box>
      {parts.slice(1).join(email)}
    </>
  );
}

function OtherAccessLinks({ variant, L, email }) {
  // Tela padrão (workspace): sem links para representante / console.
  if (variant === "workspace") return null;

  const items = [{ to: "/forgot-password", label: L.resetOtherWorkspace }];
  if (variant !== "partner") {
    items.push({ to: "/partner/forgot-password", label: L.resetOtherPartner });
  }
  if (variant !== "platform") {
    items.push({ to: "/platform/forgot-password", label: L.resetOtherPlatform });
  }
  const prefilled = String(email || "").trim();
  return (
    <Box sx={{ width: "100%", mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
        {L.resetOtherAccessTitle}
      </Typography>
      <Box display="flex" flexDirection="column" gap={0.5}>
        {items.map((item) => (
          <Link
            key={item.to}
            component={RouterLink}
            to={item.to}
            state={prefilled ? { prefilledEmail: prefilled } : undefined}
            variant="body2"
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {item.label}
          </Link>
        ))}
      </Box>
    </Box>
  );
}

function readLoginLocaleInitial() {
  if (typeof window !== "undefined") {
    const fromUrl = parseUiLocaleFromSearch(window.location.search);
    if (fromUrl) return fromUrl;
  }
  return readStoredLocale() || "pt-BR";
}

/**
 * Esqueci a senha por tipo de acesso:
 * - workspace (padrão): /forgot-password — exige identificador da empresa
 * - partner: /partner/forgot-password
 * - platform: /platform/forgot-password
 */
export default function ForgotPasswordPage({ variant = "workspace" }) {
  const brand = useBrand();
  const theme = useTheme();
  const { t } = useTranslation("app");
  const location = useLocation();
  const scrollContainerRef = useRef(null);

  const isPartner = variant === "partner";
  const isPlatform = variant === "platform";
  const isPortalScope = isPartner || isPlatform;
  const accent = isPartner ? theme.palette.secondary : theme.palette.primary;
  const loginPath = isPartner ? "/partner/login" : isPlatform ? "/platform/login" : "/login";
  const resetEndpoint = isPartner
    ? "/users/partner/send-reset"
    : isPlatform
      ? "/users/platform/send-reset"
      : "/users/send-reset";

  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [localeChoice, setLocaleChoice] = useState(readLoginLocaleInitial);
  const [footerLocaleOpen, setFooterLocaleOpen] = useState(false);

  const L = getLoginUiStrings(localeChoice);
  const intro = isPartner
    ? L.forgotPasswordIntroPartner
    : isPlatform
      ? L.forgotPasswordIntroPlatform
      : L.forgotPasswordIntro;

  useEffect(() => {
    const st = location.state;
    if (!st || typeof st !== "object") return;
    const em = typeof st.prefilledEmail === "string" ? st.prefilledEmail.trim() : "";
    const co = typeof st.prefilledCompany === "string" ? st.prefilledCompany.trim() : "";
    if (em) setEmail(em);
    if (!isPortalScope && co && COMPANY_RE.test(toSlug(co))) setCompany(toSlug(co));
  }, [location.state, isPortalScope]);

  useEffect(() => {
    const code = parseUiLocaleFromSearch(location.search);
    if (code) setLocaleChoice(code);
  }, [location.search]);

  useEffect(() => {
    setOk(false);
    setError(null);
  }, [variant]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
  }, []);

  const handleLocaleChoice = (next) => {
    const locale = String(next || "").trim() || "pt-BR";
    setLocaleChoice(locale);
    setUiLocale(locale);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const em = String(email || "").trim().toLowerCase();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setError(L.invalidEmail);
      return;
    }

    let slug = "";
    if (!isPortalScope) {
      const trimmed = String(company || "").trim();
      if (!trimmed || !COMPANY_RE.test(trimmed)) {
        setError(L.companyFieldInvalidLogin);
        return;
      }
      slug = toSlug(trimmed);
    }

    setError(null);
    setLoading(true);
    try {
      if (slug) {
        try {
          localStorage.setItem("company", slug);
          localStorage.setItem("xproWorkspaceDatabase", slug);
        } catch {
          /* ignore */
        }
      } else {
        try {
          localStorage.removeItem("company");
          localStorage.removeItem("xproWorkspaceDatabase");
        } catch {
          /* ignore */
        }
      }
      await apiClient.post(
        resetEndpoint,
        { email: em },
        {
          headers: { "Content-Type": "application/json" },
          skipCompanyHeader: isPortalScope,
          skipAuthHeader: true,
        }
      );
      setSubmittedEmail(em);
      setOk(true);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 429
          ? L.resetTooMany
          : err?.response?.data?.message ||
            err?.message ||
            "Não foi possível enviar. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const badgeKey = isPartner ? "platformPage.partnerConsoleBadge" : "platformPage.consoleBadge";

  return (
    <Box
      ref={scrollContainerRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        background: isPortalScope
          ? theme.palette.mode === "dark"
            ? `linear-gradient(155deg, ${alpha(accent.main, 0.28)} 0%, #121212 42%, #0d0d0d 100%)`
            : `linear-gradient(155deg, ${alpha(accent.main, 0.2)} 0%, #f7f7f9 38%, #ececf2 100%)`
          : theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #121212, #1e1e1e)"
            : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        p: 2,
        pt: 4,
        pb: 4,
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    >
      <MarketingMeasurementScripts />
      <Box display="flex" alignItems="center" justifyContent="center" mb={4} flexShrink={0}>
        <img
          src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
          alt={brand.name}
          style={{ height: 60 }}
          loading="lazy"
        />
      </Box>

      <Paper
        elevation={6}
        sx={{
          p: 4,
          maxWidth: 460,
          width: "90%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
          ...(isPortalScope
            ? {
                border: "1px solid",
                borderColor: (th) => alpha(accent.main, th.palette.mode === "dark" ? 0.35 : 0.25),
                boxShadow: (th) => `0 12px 40px ${alpha(accent.main, 0.12)}`,
              }
            : null),
        }}
      >
        {isPortalScope ? (
          <Chip
            label={t(badgeKey)}
            size="small"
            color={isPartner ? "secondary" : "primary"}
            variant="outlined"
            sx={{ mb: 1.5 }}
          />
        ) : null}
        <Avatar sx={{ bgcolor: accent.main, mb: 2 }}>
          <LockResetOutlined sx={{ color: theme.palette.getContrastText(accent.main) }} />
        </Avatar>

        <Typography variant="h5" gutterBottom align="center">
          {L.forgotPasswordTitle}
        </Typography>

        {!ok ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, width: "100%" }}>
            {intro}
          </Typography>
        ) : null}

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 2 }} role="alert">
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {L.resetErrorTitle}
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {ok ? (
          <>
            <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {L.resetCheckInboxTitle}
              </Typography>
              <Typography variant="body2">
                <CheckInboxBody template={L.resetCheckInboxBody} email={submittedEmail} />
              </Typography>
            </Alert>
            <Box sx={{ width: "100%", mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                {L.resetIfNoEmailTitle}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25, color: "text.secondary" }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {L.resetIfNoEmailTypo}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {L.resetIfNoEmailSpam}
                </Typography>
                {variant !== "workspace" ? (
                  <Typography component="li" variant="body2">
                    {L.resetIfNoEmailWrongPlace}
                  </Typography>
                ) : null}
              </Box>
              <OtherAccessLinks variant={variant} L={L} email={submittedEmail} />
            </Box>
            <Button
              type="button"
              fullWidth
              variant="outlined"
              color={isPartner ? "secondary" : "primary"}
              onClick={() => {
                setOk(false);
                setError(null);
              }}
              sx={{
                height: 48,
                fontWeight: "bold",
                fontSize: "1rem",
                textTransform: "none",
                mb: 1,
              }}
            >
              {L.resetTryAnotherEmail}
            </Button>
            <Button
              component={RouterLink}
              to={loginPath}
              fullWidth
              variant="contained"
              color={isPartner ? "secondary" : "primary"}
              sx={{
                height: 48,
                fontWeight: "bold",
                fontSize: "1rem",
                textTransform: "none",
              }}
            >
              {L.signupContinueToLogin}
            </Button>
          </>
        ) : (
          <Box component="form" onSubmit={handleSubmit} width="100%" display="flex" flexDirection="column" gap={1.25}>
            <TextField
              label={L.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              margin="dense"
              autoComplete="email"
            />
            {!isPortalScope ? (
              <TextField
                label={L.workspaceField}
                value={company}
                onChange={(e) => setCompany(toSlug(e.target.value))}
                required
                fullWidth
                margin="dense"
                helperText={L.signupWorkspaceHint}
              />
            ) : null}
            <Button
              type="submit"
              variant="contained"
              color={isPartner ? "secondary" : "primary"}
              disabled={loading}
              fullWidth
              sx={{
                mt: 0.75,
                height: 48,
                fontWeight: "bold",
                fontSize: "1rem",
                textTransform: "none",
              }}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              {loading ? L.sending : L.sendLink}
            </Button>
            <Button
              component={RouterLink}
              to={loginPath}
              fullWidth
              variant="text"
              sx={{ textTransform: "none" }}
            >
              {L.signupContinueToLogin}
            </Button>
            <OtherAccessLinks variant={variant} L={L} email={email} />
          </Box>
        )}
      </Paper>

      <Box
        mt={4}
        pb={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={0.75}
        sx={{ flexShrink: 0 }}
      >
        {footerLocaleOpen ? (
          <FormControl size="small" variant="standard" sx={{ minWidth: 200 }}>
            <Select
              open={footerLocaleOpen}
              onClose={() => setFooterLocaleOpen(false)}
              value={localeChoice}
              onChange={(e) => {
                handleLocaleChoice(e.target.value);
                setFooterLocaleOpen(false);
              }}
              autoFocus
              MenuProps={{
                anchorOrigin: { vertical: "top", horizontal: "center" },
                transformOrigin: { vertical: "bottom", horizontal: "center" },
              }}
              sx={{
                fontSize: "0.8125rem",
                color: theme.palette.text.secondary,
                "&::before, &::after": { display: "none" },
                "& .MuiSelect-select": { py: 0.25, pr: "24px !important" },
              }}
            >
              {getGlobalLocalesForUiSelect().map(({ code, nativeName }) => (
                <MenuItem key={code} value={code}>
                  {nativeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={() => setFooterLocaleOpen(true)}
            aria-label={L.localeLabel}
            aria-haspopup="listbox"
            sx={{
              cursor: "pointer",
              color: theme.palette.text.secondary,
              border: 0,
              background: "none",
              font: "inherit",
              p: 0,
              textDecoration: "underline",
              textDecorationColor: theme.palette.action.disabled,
              textUnderlineOffset: 3,
              "&:hover": {
                color: theme.palette.primary.main,
                textDecorationColor: theme.palette.primary.main,
              },
            }}
          >
            {getLoginLocaleLabel(localeChoice)}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center" }}>
          © {new Date().getFullYear()} {brand.name}. Todos os direitos reservados.
        </Typography>
      </Box>
    </Box>
  );
}
